import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Paper,
} from "@mui/material";
import dayjs from "../../lib/dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import {
  getPaymentsReport,
  PaymentsReportResponse,
  PaymentMethod,
  PaymentStatus,
} from "../../services/finance.service";

const ALL_METHODS: PaymentMethod[] = [
  "PIX",
  "CREDIT_CARD",
  "DEBIT_CARD",
  "BOLETO",
  "CASH",
  "TRANSFER",
];

const ALL_STATUSES: PaymentStatus[] = [
  "PAID",
  "PENDING",
  "AUTHORIZED",
  "REFUNDED",
  "CHARGEBACK",
  "CANCELLED",
  "FAILED",
];

function labelPaymentMethod(method: string) {
  switch (String(method)) {
    case "PIX":
      return "Pix";
    case "CREDIT_CARD":
      return "Cartão de crédito";
    case "DEBIT_CARD":
      return "Cartão de débito";
    case "BOLETO":
      return "Boleto";
    case "CASH":
      return "Dinheiro";
    case "TRANSFER":
      return "Transferência";
    default:
      return String(method || "-");
  }
}

function labelPaymentStatus(status: string) {
  switch (String(status)) {
    case "PAID":
      return "Pago";
    case "PENDING":
      return "Pendente";
    case "AUTHORIZED":
      return "Autorizado";
    case "REFUNDED":
      return "Reembolsado";
    case "CHARGEBACK":
      return "Chargeback";
    case "CANCELLED":
      return "Cancelado";
    case "FAILED":
      return "Falhou";
    default:
      return String(status || "-");
  }
}

type ExportFormat = "pdf" | "excel";

export function Finance() {
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [methods, setMethods] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>(["PAID"]);
  const [minValue, setMinValue] = useState<string>("");
  const [maxValue, setMaxValue] = useState<string>("");
  const [q, setQ] = useState<string>("");

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState<PaymentsReportResponse | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getPaymentsReport({
        start: startDate || undefined,
        end: endDate || undefined,
        paymentMethod: methods.length ? methods : undefined,
        status: statuses.length ? statuses : undefined,
        minValue: minValue || undefined,
        maxValue: maxValue || undefined,
        q: q || undefined,
        page: page + 1,
        perPage: rowsPerPage,
      });
      setData(res);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, methods, statuses, minValue, maxValue, q, page, rowsPerPage]);

  useEffect(() => {
    load();
  }, [load]);

  const formatMoney = (n: number) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
      Number(n ?? 0),
    );

  const formatDateTime = (iso?: string | null) =>
    iso ? dayjs(iso).format("DD/MM/YYYY HH:mm") : "-";

  const totals = data?.totals;

  const statusColor = (s: string): "default" | "success" | "warning" | "error" => {
    if (s === "PAID") return "success";
    if (s === "PENDING" || s === "AUTHORIZED") return "warning";
    if (s === "FAILED" || s === "CANCELLED" || s === "CHARGEBACK") return "error";
    return "default";
  };

  const exportPayments = async (format: ExportFormat) => {
    if (!data) return;

    const totalItems = data.pagination?.totalItems ?? data.payments.length;

    const fullData = await getPaymentsReport({
      start: startDate || undefined,
      end: endDate || undefined,
      paymentMethod: methods.length ? methods : undefined,
      status: statuses.length ? statuses : undefined,
      minValue: minValue || undefined,
      maxValue: maxValue || undefined,
      q: q || undefined,
      page: 1,
      perPage: totalItems,
    });

    const totalsRows = [
      ["Pagamentos", fullData.totals.payments],
      ["Total bruto", formatMoney(fullData.totals.gross)],
      ["Taxas", formatMoney(fullData.totals.fees)],
      ["Repasse médicos (estimado)", formatMoney(fullData.totals.doctorShare)],
      ["Plataforma (estimado)", formatMoney(fullData.totals.platformShare)],
    ];

    const rows = fullData.payments.map((p) => [
      formatDateTime(p.paidAt || p.issuedAt),
      labelPaymentStatus(String(p.status)),
      labelPaymentMethod(String(p.paymentMethod)),
      p.plan?.name ?? "-",
      p.patient?.name ?? "-",
      p.doctor?.name ?? "-",
      p.doctor?.percentual ?? "-",
      formatMoney(Number(p.value ?? 0)),
      formatMoney(Number(p.computed?.doctorShare ?? 0)),
      formatMoney(Number(p.computed?.platformShare ?? 0)),
    ]);

    const filenameBase = `relatorio-financeiro-${dayjs().format("YYYYMMDD-HHmm")}`;

    if (format === "excel") {
      const wsData = [
        ["Totais"],
        ["Métrica", "Valor"],
        ...totalsRows,
        [],
        [
          "Data",
          "Status",
          "Método",
          "Plano",
          "Paciente",
          "Médico",
          "% Médico",
          "Valor",
          "Repasse",
          "Plataforma",
        ],
        ...rows,
      ];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Financeiro");
      XLSX.writeFile(wb, `${filenameBase}.xlsx`);
      return;
    }

    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("Relatório Financeiro", 14, 16);

    (doc as any).autoTable({
      startY: 22,
      head: [["Métrica", "Valor"]],
      body: totalsRows,
    });

    const finalY = ((doc as any).lastAutoTable?.finalY || 22) + 6;

    (doc as any).autoTable({
      startY: finalY,
      head: [[
        "Data",
        "Status",
        "Método",
        "Plano",
        "Paciente",
        "Médico",
        "% Médico",
        "Valor",
        "Repasse",
        "Plataforma",
      ]],
      body: rows,
      styles: { fontSize: 8 },
    });

    doc.save(`${filenameBase}.pdf`);
  };

  const handleApplyFilters = () => {
    setPage(0);
  };

  const handleClear = () => {
    setStartDate("");
    setEndDate("");
    setMethods([]);
    setStatuses(["PAID"]);
    setMinValue("");
    setMaxValue("");
    setQ("");
    setPage(0);
  };

  const rows = data?.payments ?? [];

  const methodsLabel = useMemo(() => {
    if (!methods.length) return "Todos";
    return methods.join(", ");
  }, [methods]);

  return (
    <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto", p: 3 }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1} mb={2}>
        <Typography variant="h6" fontWeight={800} color="#133859">
          Financeiro
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button variant="outlined" onClick={handleApplyFilters} disabled={loading || exporting}>
            Aplicar
          </Button>
          <Button variant="text" color="inherit" onClick={handleClear} disabled={loading || exporting}>
            Limpar
          </Button>
          <Button
            variant="outlined"
            onClick={async () => {
              try {
                setExporting(true);
                await exportPayments("pdf");
              } finally {
                setExporting(false);
              }
            }}
            disabled={loading || exporting || !data}
          >
            Exportar PDF
          </Button>
          <Button
            variant="outlined"
            onClick={async () => {
              try {
                setExporting(true);
                await exportPayments("excel");
              } finally {
                setExporting(false);
              }
            }}
            disabled={loading || exporting || !data}
          >
            Exportar Excel
          </Button>
        </Stack>
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="subtitle2" color="text.secondary">
              Filtros
            </Typography>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Data inicial"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Data final"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Buscar (paciente, médico, plano, txid)"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Método</InputLabel>
                <Select
                  multiple
                  value={methods}
                  label="Método"
                  onChange={(e) => setMethods(e.target.value as string[])}
                  renderValue={() => methodsLabel}
                >
                  {ALL_METHODS.map((m) => (
                    <MenuItem key={m} value={m}>
                      {labelPaymentMethod(m)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  multiple
                  value={statuses}
                  label="Status"
                  onChange={(e) => setStatuses(e.target.value as string[])}
                  renderValue={(selected) => (selected as string[]).join(", ")}
                >
                  {ALL_STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {labelPaymentStatus(s)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Valor mínimo"
                value={minValue}
                onChange={(e) => setMinValue(e.target.value)}
                fullWidth
              />
              <TextField
                label="Valor máximo"
                value={maxValue}
                onChange={(e) => setMaxValue(e.target.value)}
                fullWidth
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={2}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="caption" color="text.secondary">
              Total recebido (bruto)
            </Typography>
            <Typography variant="h6" fontWeight={800}>
              {formatMoney(totals?.gross ?? 0)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {totals?.payments ?? 0} pagamentos
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="caption" color="text.secondary">
              Repasse médicos (estimado)
            </Typography>
            <Typography variant="h6" fontWeight={800}>
              {formatMoney(totals?.doctorShare ?? 0)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Baseado no percentual do médico
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="caption" color="text.secondary">
              Plataforma (estimado)
            </Typography>
            <Typography variant="h6" fontWeight={800}>
              {formatMoney(totals?.platformShare ?? 0)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Bruto - repasse
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography fontWeight={700}>Pagamentos</Typography>
            {loading && <CircularProgress size={20} />}
          </Stack>

          <Divider sx={{ mb: 1 }} />

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Método</TableCell>
                  <TableCell>Paciente</TableCell>
                  <TableCell>Médico</TableCell>
                  <TableCell>% Médico</TableCell>
                  <TableCell>Valor</TableCell>
                  <TableCell>Repasse</TableCell>
                  <TableCell>Plataforma</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      {formatDateTime(r.paidAt || r.issuedAt)}
                      {r.appointment?.startTime && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Consulta: {formatDateTime(r.appointment.startTime)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={statusColor(String(r.status))}
                        label={labelPaymentStatus(String(r.status))}
                      />
                    </TableCell>
                    <TableCell>{labelPaymentMethod(String(r.paymentMethod))}</TableCell>
                    <TableCell>{r.patient?.name ?? "-"}</TableCell>
                    <TableCell>{r.doctor?.name ?? "-"}</TableCell>
                    <TableCell>{r.doctor?.percentual ?? "-"}</TableCell>
                    <TableCell>{formatMoney(Number(r.value ?? 0))}</TableCell>
                    <TableCell>{formatMoney(Number(r.computed?.doctorShare ?? 0))}</TableCell>
                    <TableCell>{formatMoney(Number(r.computed?.platformShare ?? 0))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={data?.pagination.totalItems ?? 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 20, 50]}
          />
        </CardContent>
      </Card>
    </Box>
  );
}
